import { toast } from "react-hot-toast";
import i18n from "@/i18n/config";

type ToastType = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  duration?: number;
}

// Custom styles for the types without react-hot-toast built-ins
const TOAST_STYLES = {
  warning: {
    icon: "⚠️",
    style: {
      background: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FBBF24",
    },
  },
  info: {
    icon: "ℹ️",
    style: {
      background: "#DBEAFE",
      color: "#1E40AF",
      border: "1px solid #3B82F6",
    },
  },
} as const;

// Placement is controlled by the app-level <Toaster> (see AppToaster).
const DEFAULT_OPTIONS: Required<ToastOptions> = {
  duration: 4000,
};

class ToastService {
  private showToast(
    message: string,
    type: ToastType,
    options?: ToastOptions,
  ): string {
    const mergedOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    if (type === "success" || type === "error") {
      // Use react-hot-toast built-in success/error methods
      return type === "success"
        ? toast.success(message, mergedOptions)
        : toast.error(message, mergedOptions);
    }

    // Use custom styling for warning and info
    return toast(message, {
      ...mergedOptions,
      ...TOAST_STYLES[type],
    });
  }

  success(message: string, options?: ToastOptions): string {
    return this.showToast(message, "success", options);
  }

  error(message: string, options?: ToastOptions): string {
    return this.showToast(message, "error", options);
  }

  warning(message: string, options?: ToastOptions): string {
    return this.showToast(message, "warning", options);
  }

  info(message: string, options?: ToastOptions): string {
    return this.showToast(message, "info", options);
  }

  dismiss(toastId?: string): void {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  }

  // Session expired toast with specific styling
  sessionExpired(): string {
    return this.warning(i18n.t("auth:sessionExpired"));
  }
}

// Export singleton instance
export const toastService = new ToastService();
